import React from 'react';
import classNames from 'classnames';
import styles from './MediaGuideBanner.module.scss';

/** Ce que la fiche en cours de création dit de sa saisie, pour que l'accompagnement avance. */
export interface MediaGuideState {
  title          : string;
  categoryChosen : boolean;
  /** Vidéo seulement : la question de la sortie a reçu une réponse. */
  releaseChosen? : boolean;
}

export type MediaGuideStep = 'title' | 'category' | 'release' | 'done';

/** Seule la vidéo a une catégorie et une sortie : les autres types n'ont que le nom à donner. */
export const guideStepOf = (state: MediaGuideState, needsCategory: boolean): MediaGuideStep => {
  if (state.title.trim() === '') return 'title';
  if (needsCategory && !state.categoryChosen) return 'category';
  if (needsCategory && !state.releaseChosen) return 'release';
  return 'done';
};

export interface MediaGuideBannerProps {
  heading       : string;
  state         : MediaGuideState;
  needsCategory : boolean;
  /** Dernière étape : le texte dépend du type (une diapositive attend encore son image et son son). */
  doneMessage   : string;
  /** Absent quand le type est imposé sans dépôt (diapositive d'un sélecteur filtré) : rien à changer. */
  onChangeMedia?: () => void;
  /** La fiche vidéo y projette ses boutons Annuler et Créer, pour qu'ils restent toujours visibles. */
  actionsRef?   : (element: HTMLDivElement | null) => void;
}

const STEP_MESSAGE: Record<Exclude<MediaGuideStep, 'done'>, string> = {
  title    : 'Donnez un nom',
  category : 'Choisissez une catégorie',
  release  : 'Indiquez la sortie',
};

export const MediaGuideBanner = ({ heading, state, needsCategory, doneMessage, onChangeMedia, actionsRef }: MediaGuideBannerProps) => {
  const step  = guideStepOf(state, needsCategory);
  const steps: { key: MediaGuideStep; label: string }[] = needsCategory
    ? [{ key: 'title', label: 'Nom' }, { key: 'category', label: 'Catégorie' }, { key: 'release', label: 'Sortie' }]
    : [{ key: 'title', label: 'Nom' }];
  const currentIndex = steps.findIndex(s => s.key === step);

  return (
    <div className={classNames(styles.root)}>
      <h1 className={classNames(styles.heading)}>{heading}</h1>
      <ol className={classNames(styles.steps)}>
        {steps.map((s, index) => {
          const done    = currentIndex === -1 || index < currentIndex;
          const current = index === currentIndex;
          return (
            <li key={s.key} className={classNames(styles.step, current && styles.step_current, done && styles.step_done)}>
              <span className={classNames(styles.mark)} aria-hidden="true">{done ? '✓' : index + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>
      <span className={classNames(styles.message)} role="status">
        {step === 'done' ? doneMessage : STEP_MESSAGE[step]}
      </span>
      {onChangeMedia && (
        <button type="button" className={classNames(styles.change_button)} onClick={onChangeMedia}>
          Changer de média
        </button>
      )}
      {actionsRef && <div ref={actionsRef} />}
    </div>
  );
};

export default MediaGuideBanner;
